import { ORG_STATUS_REASON_MAX, type OrgStatusAction } from '@carcheck/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

/**
 * Suspend / reinstate / terminate confirm (AUT-403), after the mockup's
 * `?state=suspend` / `?state=terminate` modals. Every action takes a reason
 * (it lands in `admin_logs`). Terminate is FINAL — no reinstate, no edit,
 * codes revoked, members detached — so it also asks for the company name
 * typed back. The mockup's «нужен TOTP» is not wired: the API does not
 * require a step-up for these routes.
 */
const POINTS = ['p1', 'p2', 'p3'] as const;

const buildSchema = (phrase: string | null) =>
  z.object({
    reason: z
      .string()
      .trim()
      .min(1, 'companies.statusDialog.reasonRequired')
      .max(ORG_STATUS_REASON_MAX, 'companies.statusDialog.reasonMax'),
    phrase: z
      .string()
      .refine((v) => phrase === null || v.trim() === phrase, 'confirm.phraseMismatch'),
  });

type StatusForm = { reason: string; phrase: string };

const Warning = ({ ns, danger }: { ns: string; danger: boolean }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Alert severity={danger ? 'error' : 'warning'}>
      <b>{t(`${ns}.lead`)}</b>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {POINTS.map((p) => (
          <li key={p}>{t(`${ns}.${p}`)}</li>
        ))}
      </ul>
    </Alert>
  );
};

/** Reason (+ the typed-back name for terminate); cleared on every open. */
const useStatusForm = (action: OrgStatusAction | null, companyName: string) => {
  const phrase = action === 'terminate' ? companyName : null;
  const schema = useMemo(() => buildSchema(phrase), [phrase]);
  const form = useForm<StatusForm>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { reason: '', phrase: '' },
  });
  useEffect(() => {
    if (action !== null) form.reset({ reason: '', phrase: '' });
  }, [action, form]);
  return form;
};

export interface StatusActionDialogProps {
  action: OrgStatusAction | null;
  companyName: string;
  activeMembers: number;
  loading: boolean;
  onConfirm: (action: OrgStatusAction, reason: string) => void;
  onCancel: () => void;
}

export const StatusActionDialog = ({
  action,
  companyName,
  activeMembers,
  loading,
  onConfirm,
  onCancel,
}: StatusActionDialogProps): ReactElement => {
  const { t } = useTranslation();
  const terminate = action === 'terminate';
  const form = useStatusForm(action, companyName);
  const { errors, isValid } = form.formState;

  const ns = `companies.statusDialog.${action ?? 'suspend'}`;
  const submit = ({ reason }: StatusForm): void => {
    if (action !== null && !loading) onConfirm(action, reason.trim());
  };

  return (
    <Dialog open={action !== null} onClose={loading ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t(`${ns}.title`)}
        <Typography variant="body2" color="text.secondary">
          {t('companies.statusDialog.subject', { name: companyName, count: activeMembers })}
        </Typography>
      </DialogTitle>
      <form noValidate onSubmit={form.handleSubmit(submit)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Warning ns={ns} danger={terminate} />
          {terminate ? (
            <TextField
              {...form.register('phrase')}
              size="small"
              fullWidth
              disabled={loading}
              label={t('companies.statusDialog.phraseLabel')}
              placeholder={companyName}
              slotProps={{ htmlInput: { 'data-testid': 'company-status-phrase-input' } }}
              error={Boolean(errors.phrase)}
              helperText={errors.phrase ? t('confirm.phraseMismatch') : ' '}
            />
          ) : null}
          <TextField
            {...form.register('reason')}
            fullWidth
            multiline
            minRows={2}
            size="small"
            disabled={loading}
            label={t('companies.statusDialog.reasonLabel')}
            error={Boolean(errors.reason)}
            helperText={
              errors.reason
                ? t(errors.reason.message ?? '', { max: ORG_STATUS_REASON_MAX })
                : t('companies.statusDialog.reasonHint')
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color={terminate ? 'error' : action === 'suspend' ? 'warning' : 'primary'}
            disabled={loading || !isValid}
            loading={loading}
            data-testid="company-status-confirm"
          >
            {t(`${ns}.confirm`)}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
